import { Bell, Cake, Calendar, CalendarClock, Clock, Timer } from "lucide-react"

import { FrontendTriggerDefinition } from "@/components/automations/types"

const meetingReminderIntervalOptions = [
  { value: "minutes_30", label: "30 minutes before" },
  { value: "hour_1", label: "1 hour before" },
  { value: "hours_2", label: "2 hours before" },
  { value: "hours_24", label: "24 hours before" },
  { value: "morning_of", label: "Morning of" },
  { value: "day_1", label: "Day before" },
  { value: "days_2", label: "2 days before" },
  { value: "days_3", label: "3 days before" },
  { value: "days_5", label: "5 days before" },
  { value: "week_1", label: "1 week before" },
  { value: "weeks_2", label: "2 weeks before" },
  { value: "weeks_3", label: "3 weeks before" },
]

const reminderIntervalOptions = [
  { value: "0", label: "Same day" },
  { value: "1", label: "1 day before" },
  { value: "2", label: "2 days before" },
  { value: "3", label: "3 days before" },
  { value: "5", label: "5 days before" },
  { value: "7", label: "1 week before" },
  { value: "14", label: "2 weeks before" },
  { value: "21", label: "3 weeks before" },
  { value: "30", label: "1 month before" },
  { value: "60", label: "2 months before" },
  { value: "90", label: "3 months before" },
  { value: "180", label: "6 months before" },
]

const customDateReminderIntervalOptions = [...reminderIntervalOptions, { value: "365", label: "1 year before" }]

const birthdayPersonTypeOptions = [
  { value: "head_of_household", label: "Head of household" },
  { value: "spouse", label: "Spouse" },
  { value: "partner", label: "Partner" },
  { value: "dependent_child", label: "Dependent child" },
  { value: "non_dependent_child", label: "Non-dependent child" },
  { value: "grandchild", label: "Grandchild" },
  { value: "other_adult", label: "Other adult" },
  { value: "other_dependent", label: "Other dependent" },
]

const birthdayHouseholdTypeOptions = [
  { value: "client", label: "Clients" },
  { value: "prospect", label: "Prospects" },
  { value: "both", label: "Clients and prospects" },
]

const customDateHouseholdTypeOptions = [
  { value: "client", label: "Clients" },
  { value: "prospect", label: "Prospects" },
]

const ageMilestoneOptions = [
  { value: "50", label: "50" },
  { value: "55", label: "55" },
  { value: "59.5", label: "59.5" },
  { value: "62", label: "62" },
  { value: "65", label: "65" },
  { value: "67", label: "67" },
  { value: "70", label: "70" },
  { value: "73", label: "73" },
  { value: "75", label: "75" },
]

const ageMilestoneLookAheadOptions = [
  { value: "1", label: "Week" },
  { value: "2", label: "2 weeks" },
  { value: "3", label: "3 weeks" },
  { value: "4", label: "4 weeks" },
]

export const reminderTriggers: FrontendTriggerDefinition[] = [
  {
    key: "trigger__birthday_reminder",
    label: "Birthday reminder",
    description: "Triggers X days before a client or prospect's birthday",
    icon: Cake,
    providesSubjects: ["person", "household"],
    inputFields: [
      {
        key: "day_offset",
        label: "Days before birthday",
        inputType: "select",
        required: true,
        defaultValue: "7",
        options: reminderIntervalOptions,
      },
      {
        key: "household_type",
        label: "Household type",
        inputType: "select",
        required: true,
        defaultValue: "client",
        options: birthdayHouseholdTypeOptions,
      },
      {
        key: "household_roles",
        label: "Person types",
        inputType: "multi_select",
        required: true,
        defaultValue: ["head_of_household", "spouse", "partner"],
        options: birthdayPersonTypeOptions,
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "person",
      },
    ],
  },
  {
    key: "trigger__age_milestone_reminder",
    label: "Age milestone",
    description: "Triggers Mondays at 8 AM in the book's timezone for clients reaching a specific age",
    icon: Cake,
    providesSubjects: ["person", "household"],
    inputFields: [
      {
        key: "age_milestone",
        label: "Age milestone",
        inputType: "select",
        required: true,
        defaultValue: "65",
        options: ageMilestoneOptions,
        allowCustomValue: true,
        customValueLabel: "Custom age",
        customValuePlaceholder: "Enter age",
      },
      {
        key: "look_ahead_weeks",
        label: "For all clients in the next...",
        inputType: "select",
        required: true,
        defaultValue: "1",
        options: ageMilestoneLookAheadOptions,
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "person",
      },
    ],
  },
  {
    key: "trigger__annual_review_reminder",
    label: "Client review reminder",
    description: "Triggers X days before a client's annual review",
    icon: Calendar,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "day_offset",
        label: "Days before review date",
        inputType: "select",
        required: true,
        defaultValue: "14",
        options: reminderIntervalOptions,
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
      },
    ],
  },
  {
    key: "trigger__client_anniversary_reminder",
    label: "Client anniversary reminder",
    description: "Triggers X days before client anniversary",
    icon: Bell,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "day_offset",
        label: "Days before anniversary",
        inputType: "select",
        required: true,
        defaultValue: "7",
        options: reminderIntervalOptions,
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
      },
    ],
  },
  {
    key: "trigger__client_touchpoint_reminder",
    label: "Client touchpoint reminder",
    description: "Triggers relative to a client's next touchpoint due date",
    icon: Timer,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "day_offset",
        label: "Days before touchpoint due date",
        inputType: "select",
        required: true,
        defaultValue: "0",
        options: reminderIntervalOptions,
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
      },
    ],
  },
  {
    key: "trigger__custom_date_reminder",
    label: "Household custom date reminder",
    description: "Triggers relative to a client or prospect date custom field",
    icon: CalendarClock,
    providesSubjects: ["household"],
    inputFields: [
      {
        key: "household_type",
        label: "Household type",
        inputType: "select",
        required: true,
        defaultValue: "client",
        options: customDateHouseholdTypeOptions,
      },
      {
        key: "custom_field_id",
        label: "Date field",
        inputType: "select",
        required: true,
        options: "dynamic_date_custom_fields",
      },
      {
        key: "ignore_year",
        label: "Match type",
        inputType: "select",
        required: true,
        defaultValue: "true",
        options: [
          { value: "true", label: "Annual (same month/day every year)" },
          { value: "false", label: "Exact date (specific year)" },
        ],
      },
      {
        key: "day_offset",
        label: "Days before date",
        inputType: "select",
        required: true,
        defaultValue: "7",
        options: customDateReminderIntervalOptions,
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "household",
      },
    ],
  },
  {
    key: "trigger__meeting_reminder",
    label: "Meeting reminder",
    description: "Triggers before a scheduled meeting",
    icon: Clock,
    providesSubjects: ["meeting", "household"],
    inputFields: [
      {
        key: "reminder_interval",
        label: "When to remind",
        inputType: "select",
        required: true,
        defaultValue: "hour_1",
        options: meetingReminderIntervalOptions,
      },
      {
        key: "meeting_types",
        label: "Meeting type",
        inputType: "multi_select",
        required: false,
        options: "dynamic_meeting_types",
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "meeting",
      },
    ],
  },
]
